import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import {
  listCalculationErrors,
  type CalculationErrorEntry,
  type CalculationErrorsResponse,
} from '../services/adminService';

const ERROR_TYPES = [
  { value: '', label: 'All Error Types' },
  { value: 'FORMULA_EXECUTION', label: 'Formula Execution' },
  { value: 'MISSING_VARIABLE', label: 'Missing Variable' },
  { value: 'TIMEOUT', label: 'Timeout' },
  { value: 'DIVISION_BY_ZERO', label: 'Division by Zero' },
  { value: 'TYPE_ERROR', label: 'Type Error' },
  { value: 'INVALID_RESULT', label: 'Invalid Result' },
];

export default function CalculationErrorsPage() {
  // Filters
  const [errorType, setErrorType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');

  // Data
  const [data, setData] = useState<CalculationErrorsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Stack trace modal
  const [selectedError, setSelectedError] = useState<CalculationErrorEntry | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await listCalculationErrors({
        errorType: errorType || undefined,
        dateFrom: dateFrom || undefined,
        dateTill: dateTill || undefined,
        page,
        limit,
      });
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load errors');
    } finally {
      setIsLoading(false);
    }
  }, [errorType, dateFrom, dateTill, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = () => {
    setPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setErrorType('');
    setDateFrom('');
    setDateTill('');
    setPage(1);
  };

  const formatDate = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="admin-review-page">
      <div className="page-header">
        <h2>Calculation Errors</h2>
      </div>

      {/* Filters */}
      <section className="review-filters">
        <Select
          label="Error Type"
          options={ERROR_TYPES}
          value={errorType}
          onChange={(e) => setErrorType(e.target.value)}
        />
        <Input
          label="Date From"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <Input
          label="Date Till"
          type="date"
          value={dateTill}
          onChange={(e) => setDateTill(e.target.value)}
        />
        <div className="filter-actions">
          <Button onClick={handleFilter}>Apply Filters</Button>
          <Button variant="secondary" onClick={handleClearFilters}>Clear</Button>
        </div>
      </section>

      {error && <Alert type="error">{error}</Alert>}

      {isLoading ? (
        <LoadingSpinner message="Loading errors..." />
      ) : (
        <>
          <div className="review-summary">
            <span>Total: {data?.pagination.total ?? 0} errors</span>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ApptID</th>
                  <th>Error Type</th>
                  <th>Error Message</th>
                  <th>Failed Parameter</th>
                  <th>Hospital</th>
                  <th>Occurred At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(!data || data.errors.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="table-empty-cell">
                      No calculation errors found.
                    </td>
                  </tr>
                ) : (
                  data.errors.map((entry) => (
                    <tr key={entry.errorId}>
                      <td>{entry.apptId}</td>
                      <td>
                        <span className="error-type-badge">{entry.errorType}</span>
                      </td>
                      <td className="remarks-cell" title={entry.errorMessage}>
                        {entry.errorMessage?.length > 80
                          ? entry.errorMessage.substring(0, 80) + '...'
                          : entry.errorMessage}
                      </td>
                      <td>{entry.failedParameter || '—'}</td>
                      <td>{entry.hospitalName || '—'}</td>
                      <td>{formatDate(entry.occurredAt)}</td>
                      <td className="actions-cell">
                        <Button
                          size="sm"
                          onClick={() => setSelectedError(entry)}
                          disabled={!entry.stackTrace}
                        >
                          View Stack Trace
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav className="table-pagination" aria-label="Pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </nav>
          )}
        </>
      )}

      {/* Stack Trace Modal */}
      <Modal
        isOpen={!!selectedError}
        onClose={() => setSelectedError(null)}
        title="Error Details"
        size="lg"
      >
        {selectedError && (
          <div className="error-detail">
            <div className="calc-detail-grid">
              <div className="calc-detail-item">
                <label>Error ID</label>
                <span>{selectedError.errorId}</span>
              </div>
              <div className="calc-detail-item">
                <label>ApptID</label>
                <span>{selectedError.apptId}</span>
              </div>
              <div className="calc-detail-item">
                <label>Error Type</label>
                <span>{selectedError.errorType}</span>
              </div>
              <div className="calc-detail-item">
                <label>Failed Parameter</label>
                <span>{selectedError.failedParameter || '—'}</span>
              </div>
              <div className="calc-detail-item">
                <label>Hospital</label>
                <span>{selectedError.hospitalName || '—'}</span>
              </div>
              <div className="calc-detail-item">
                <label>Occurred At</label>
                <span>{formatDate(selectedError.occurredAt)}</span>
              </div>
            </div>
            <div className="calc-detail-remarks">
              <label>Error Message</label>
              <p>{selectedError.errorMessage}</p>
            </div>
            {selectedError.failedFormula && (
              <div className="calc-detail-remarks">
                <label>Failed Formula</label>
                <pre className="stack-trace-pre">{selectedError.failedFormula}</pre>
              </div>
            )}
            {selectedError.stackTrace && (
              <div className="calc-detail-remarks">
                <label>Stack Trace</label>
                <pre className="stack-trace-pre">{selectedError.stackTrace}</pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
